import { almNamespace } from 'src/lib/almNamespace';
import { developmentMode } from 'src/lib/localData';
import { readItem } from 'src/lib/storage';

/**
 * Read environment variable
 * Note: Environment variables overrides may be passed through "window.env" object
 *
 * @param {string} varName - variable name
 * @param {string} defaultValue - default value
 * @returns {string} - variable value
 */
const readVariable = (varName: string, defaultValue: string): string => {
  if (window.env && window.env[varName]) {
    return window.env[varName];
  }
  return defaultValue;
};

export function getAppEnv(): string {
  return readVariable('VITE_ENV', import.meta.env.VITE_ENV);
}

export function getBackendUrl(): string {
  let reactAppBackendURL = getBackendRootUrl();
  if (import.meta.env.VITE_ENV === 'development') {
    //get override from local storage
    const overrideURL = readItem('BE_LOCAL_OVERRIDE');
    if (overrideURL) {
      // eslint-disable-next-line no-console
      console.log('Backend url was overridden & set to:', overrideURL);
      reactAppBackendURL = overrideURL;
    }
  }
  return reactAppBackendURL + '/content_creator_platform';
}

export function getBackendRootUrl(): string {
  return readVariable('VITE_BACKEND', import.meta.env.VITE_BACKEND);
}

export function getRepositoryTag(): string {
  return import.meta.env.VITE_REPOSITORY_TAG;
}

export function getCommitHash(): string {
  return import.meta.env.VITE_COMMIT_HASH;
}

export function getInfinityExtensionID(): string {
  return readVariable('VITE_INFINITY_EXTENSION_ID', import.meta.env.VITE_INFINITY_EXTENSION_ID);
}

export function getFacebookAppID(): string {
  return readVariable('VITE_FACEBOOK_APP_ID', import.meta.env.VITE_FACEBOOK_APP_ID);
}

export function getSentryDSN(): string {
  return readVariable('VITE_SENTRY_DSN', import.meta.env.VITE_SENTRY_DSN);
}

export function getUnsplashID(): string {
  return readVariable('VITE_UNSPLASH_ID', import.meta.env.VITE_UNSPLASH_ID);
}

export function getStripeApiKey(): string {
  return readVariable('VITE_STRIPE_API_KEY', import.meta.env.VITE_STRIPE_API_KEY);
}

export function getWebsiteOrigin(): string {
  return readVariable('VITE_WEBSITE_ORIGIN', import.meta.env.VITE_WEBSITE_ORIGIN);
}

export function getLogRocketAppId(): string {
  return readVariable('VITE_LOG_ROCKET_APP_ID', import.meta.env.VITE_LOG_ROCKET_APP_ID);
}

export function getGtmId(): string {
  return readVariable('VITE_GTM_ID', import.meta.env.VITE_GTM_ID);
}

/**
 * When true (e.g. in .env.local for tunnel testing), allow bootstrap on any host (ngrok, etc.).
 * Use for local builds served via ngrok or other tunnels.
 */
export function getAcceptTunnelHosts(): boolean {
  const v = readVariable(
    'VITE_ACCEPT_TUNNEL_HOSTS',
    import.meta.env.VITE_ACCEPT_TUNNEL_HOSTS ?? ''
  );
  return v === 'true';
}

export function getAdobeExpressClientId(): string {
  return readVariable('VITE_ADOBE_EXPRESS_CLIENT_ID', import.meta.env.VITE_ADOBE_EXPRESS_CLIENT_ID);
}

/**
 * Check if the app is running in development mode
 * @returns {boolean}
 */
export const inDevelopmentMode = (): boolean => {
  try {
    if (developmentMode.read()) {
      return true;
    }
  } catch {
    // do  nothing
  }

  return getAppEnv() === 'development';
};

/**
 * Enable development mode
 */
export const enableDevelopmentMode = (): void => {
  try {
    developmentMode.write(true);
  } catch {
    // do nothing
  }
};

/**
 * Disable development mode
 */
export const disableDevelopmentMode = (): void => {
  try {
    developmentMode.remove();
  } catch {
    // do nothing
  }
};

almNamespace.add('developmentMode', {
  enableDevelopmentMode,
  disableDevelopmentMode,
});
